import React, { useCallback, useEffect, useState } from "react";
import { ShieldCheck, ChevronDown, ChevronRight, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const fmtDateTime = (s) => (s ? new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");
const fmtDate = (s) => (s ? String(s).slice(0, 10) : "—");

const SOURCE_LABEL = { base: "None (free VIP)", license: "License grant", stripe: "Stripe subscription" };

function GrantRow({ g }) {
  return (
    <div
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border px-3 py-2 text-xs ${g.is_winner ? "border-primary/50 bg-primary/5" : "border-border/50"}`}
      data-testid="grant-row"
    >
      <span className="font-[640] text-foreground" data-testid="grant-tier">{g.tier_name}</span>
      <Badge variant="outline" className="h-5 px-1.5 text-[10px] uppercase">{g.status}</Badge>
      <span className="text-muted-foreground">{g.access_source || g.source || "—"}</span>
      {g.reason_issued && <span className="text-muted-foreground">· {g.reason_issued}</span>}
      <span className="text-muted-foreground">· {g.duration_type || "—"}</span>
      {g.expires_at && <span className="text-amber-600">· expires {fmtDate(g.expires_at)}</span>}
      {g.redeemed_at && <span className="text-muted-foreground">· redeemed {fmtDate(g.redeemed_at)}</span>}
      <span className="ml-auto flex items-center gap-2">
        <span className="font-mono text-[10px] text-muted-foreground/70" title="Safe key reference (not the key)">#{(g.id || "").slice(0, 8)}</span>
        {g.counts_toward_entitlement && <Badge className="h-5 bg-teal-50 px-1.5 text-[10px] text-teal-700">counts</Badge>}
        {g.is_winner && <Badge className="h-5 bg-primary px-1.5 text-[10px] text-primary-foreground" data-testid="grant-winner-badge">winning</Badge>}
      </span>
    </div>
  );
}

export function GrantsPanel({ uid, refreshSignal }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${uid}/grants`);
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => { load(); }, [load, refreshSignal]);

  return (
    <Card className="p-5" data-testid="grants-panel">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-base font-[680] text-foreground">
          <ShieldCheck className="h-4 w-4 text-muted-foreground" /> Access &amp; License Keys
        </h3>
        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={load} title="Refresh" data-testid="grants-refresh">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Read-only. Explains the member&apos;s effective tier via the precedence resolver. Raw license keys are never shown here.
      </p>

      {loading && !data && <p className="text-sm text-muted-foreground">Loading…</p>}
      {!loading && !data && <p className="text-sm text-muted-foreground">Could not load grants.</p>}

      {data && (
        <div className="space-y-4">
          {/* tier summary */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg bg-secondary/40 p-3 text-sm">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Cached tier</p>
              <p className="font-[640] text-foreground" data-testid="grants-cached-tier">{data.cached_tier_name}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Resolver tier</p>
              <p className="font-[640] text-foreground" data-testid="grants-resolved-tier">{data.resolved_tier_name}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Winning source</p>
              <p className="font-[640] text-foreground" data-testid="grants-winning-source">{SOURCE_LABEL[data.winning_source] || data.winning_source}</p>
            </div>
            <div className="ml-auto" data-testid="grants-insync">
              {data.in_sync ? (
                <Badge className="bg-teal-50 text-teal-700"><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> In sync</Badge>
              ) : (
                <Badge className="bg-amber-50 text-amber-700"><AlertTriangle className="mr-1 h-3.5 w-3.5" /> Out of sync</Badge>
              )}
            </div>
          </div>

          {/* stripe */}
          {data.stripe && (
            <div className="rounded-md border border-border/50 px-3 py-2 text-xs" data-testid="grants-stripe">
              <span className="font-[640] text-foreground">Stripe subscription</span>
              <span className="ml-2 text-muted-foreground">
                {data.stripe.tier_name || "—"} · {data.stripe.status || "—"}
                {data.stripe.interval ? ` · ${data.stripe.interval}` : ""}
              </span>
              {data.stripe.counts && <Badge className="ml-2 h-5 bg-teal-50 px-1.5 text-[10px] text-teal-700">counts</Badge>}
            </div>
          )}

          {/* active grants */}
          <div>
            <p className="mb-1.5 text-xs font-[640] text-foreground">Active grants ({data.active_grants.length})</p>
            <div className="space-y-1.5" data-testid="grants-active-list">
              {data.active_grants.length === 0 && <p className="text-xs text-muted-foreground">No active grants.</p>}
              {data.active_grants.map((g) => <GrantRow key={g.id} g={g} />)}
            </div>
          </div>

          {/* inactive grants (collapsible) */}
          {data.inactive_grants.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setShowInactive((v) => !v)}
                className="flex items-center gap-1 text-xs font-[640] text-muted-foreground hover:text-foreground"
                data-testid="grants-inactive-toggle"
              >
                {showInactive ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                Inactive / expired / revoked ({data.inactive_grants.length})
              </button>
              {showInactive && (
                <div className="mt-1.5 space-y-1.5" data-testid="grants-inactive-list">
                  {data.inactive_grants.map((g) => <GrantRow key={g.id} g={g} />)}
                </div>
              )}
            </div>
          )}

          {/* audit */}
          <div>
            <p className="mb-1.5 text-xs font-[640] text-foreground">Recent entitlement audit</p>
            <div className="max-h-56 space-y-1.5 overflow-y-auto pr-1" data-testid="grants-audit-list">
              {data.audit.length === 0 && <p className="text-xs text-muted-foreground">No entitlement changes recorded.</p>}
              {data.audit.map((a, i) => (
                <div key={i} className="border-b border-border/40 pb-1.5 text-xs" data-testid="grants-audit-row">
                  <p className="text-foreground">
                    <span className="font-[600]">{a.prev_tier_name} → {a.new_tier_name}</span>
                    {!a.changed && <span className="text-muted-foreground"> (no change)</span>}
                    <span className="text-muted-foreground"> · won by {SOURCE_LABEL[a.winning_source] || a.winning_source}</span>
                  </p>
                  <p className="text-muted-foreground">
                    {a.trigger}{a.actor_name ? ` · by ${a.actor_name}` : ""}{a.reason ? ` · ${a.reason}` : ""} · {fmtDateTime(a.created_at)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
